import { useRef, useState, type ChangeEvent } from 'react';
import attrAccept from 'attr-accept';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import type { MessageKey } from '@/modules/localizations/translations/types';
import { actions as messagesActions } from '@/modules/messages';

import type { Fields } from '../constants';
import type { SecretFormValues } from '../schemas';
import { MAX_IMPORT_FILE_SIZE, readFileAsText } from '../utilities';

export interface UseKeyImportOptions {
    name: Fields.PRIVATE_KEY | Fields.PUBLIC_KEY;
    accept?: string;
    extractKey: (text: string) => string | null;
    notFoundMessageId: MessageKey;
}

export const useKeyImport = ({ name, accept, extractKey, notFoundMessageId }: UseKeyImportOptions) => {
    const dispatch = useDispatch();

    const { setValue } = useFormContext<SecretFormValues>();
    const value = useWatch<SecretFormValues>({ name }) as string | undefined;

    const inputRef = useRef<HTMLInputElement>(null);

    const [fileName, setFileName] = useState<string | null>(null);
    const [errorMessageId, setErrorMessageId] = useState<MessageKey | null>(null);

    const importedValue = useRef<string | null>(null);
    const valueBeforeImport = useRef('');
    const valueAtError = useRef('');

    const showError = (messageId: MessageKey) => {
        valueAtError.current = value ?? '';
        setErrorMessageId(messageId);
    };

    const importFile = async (file: File) => {
        if (accept && !attrAccept(file, accept)) {
            // The previous file's message no longer describes what just happened.
            setErrorMessageId(null);
            dispatch(
                messagesActions.displayWarningMessage({
                    message: {
                        id: 'secret.sshKey.import.unsupportedFile',
                    },
                }),
            );
            return;
        }

        if (file.size > MAX_IMPORT_FILE_SIZE) {
            showError('secret.sshKey.import.fileTooLarge');
            return;
        }

        let text: string;

        try {
            text = await readFileAsText(file);
        } catch {
            showError('secret.sshKey.import.readFailed');
            return;
        }

        const extractedKey = extractKey(text);

        if (extractedKey === null) {
            showError(notFoundMessageId);
            return;
        }

        valueBeforeImport.current = value ?? '';
        setValue(name, extractedKey, { shouldDirty: true });
        importedValue.current = extractedKey;
        setFileName(file.name);
        setErrorMessageId(null);
    };

    return {
        // The file name only describes the field while the field still holds exactly what the file put there.
        fileName: fileName !== null && value === importedValue.current ? fileName : null,
        // The message only describes the field while the field still holds what the failed import left there.
        errorMessageId: errorMessageId !== null && value === valueAtError.current ? errorMessageId : null,
        openFilePicker: () => {
            inputRef.current?.click();
        },
        removeImport: () => {
            setValue(name, valueBeforeImport.current, { shouldDirty: true });
            importedValue.current = null;
            setFileName(null);
            setErrorMessageId(null);
        },
        inputProps: {
            ref: inputRef,
            type: 'file' as const,
            // Without `accept` the dialog shows every file - deliberate for private keys,
            // whose ssh-keygen defaults (id_rsa, id_ed25519) carry no extension at all.
            ...(accept ? { accept } : {}),
            multiple: false,
            style: { display: 'none' },
            'data-testid': 'dropzone-input',
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];

                if (file) void importFile(file);

                event.target.value = '';
            },
        },
    };
};
